import {
    ExtensionNodeCommand,
    ExtensionSelectionCommand,
} from '@baidu/realtime-editor';
import * as JsonBuilder from '@baidu/morpho-json-builder';
import type {IKuToolSDKInstance} from '../types/global';

// 不需要 editor 实例参数的 MorphoAPI 接口名称列表
const noNeedGiveEditorParamMorphoApiNameList = [
    'getFragmentString',
];

/**
 * 获取编辑器实例
 */
function getEditor(store: any) {
    const rte = store.getEditorRef().current;
    const editor = rte?.getEditorInstance?.();
    return editor;
}

/**
 * 获取 MorphoAPI 实例
 * 每个接口需要重写，主要是为了兼容文档组件的动态加载的底层逻辑，每个接口需要动态获取 editor 实例
 * @param store 知识库 store 实例
 */
function getMorphoAPI(store: any): IKuToolSDKInstance['editor']['MorphoAPI'] {
    // 将首个参数需要传 editor 实例的这些函数，统一封装一层，传入 editor 实例，这样，插件业务侧调用时，就不需要传入 editor 实例
    const editor = getEditor(store);
    if (!editor?.MorphoAPI) {
        return {};
    }

    const morphoAPIKeys = Object.keys(editor.MorphoAPI);
    const MorphoAPI: Record<string, () => any> = {};
    morphoAPIKeys.forEach((apiName) => {
        MorphoAPI[apiName] = (...args: any[]) => {
            const editor = getEditor(store);
            const apiFunction = editor?.MorphoAPI?.[apiName];
            if (!apiFunction) {
                throw new Error(`[文档组件] MorphoAPI 未找到接口: ${apiName}`);
            }

            if (noNeedGiveEditorParamMorphoApiNameList.includes(apiName)) {
                return apiFunction.apply(
                    undefined,
                    args
                );
            }

            return apiFunction.apply(
                // MorphoAPI 存在有函数使用了 this.editor，实际上拿不到 editor 实例，刚好这里顺便处理一下
                {editor},
                [editor, ...args]
            );
        };
    });
    return MorphoAPI;
}

let editorCacheForNodeCommand: any = null;
const nodeCommandCache: ExtensionNodeCommand | null = null;
function getExtensionNodeCommand(store: any): ExtensionNodeCommand {
    const editor = getEditor(store);
    // 缓存非空，且最新获取的编辑器实例与缓存一致时，直接取缓存的 nodeCommand 实例
    if (nodeCommandCache && (editor === editorCacheForNodeCommand)) {
        return nodeCommandCache;
    }

    // 记录一下最新获取的编辑器实例，作为下一次缓存判断的依据
    editorCacheForNodeCommand = editor;
    const nodeCommand = new ExtensionNodeCommand(editor);
    return nodeCommand;
}

function getNode(store: any): IKuToolSDKInstance['editor']['node'] {
    const editor = getEditor(store);

    // 理论上 editor 实例不可能为空，此处仅做保护性兜底处理
    if (!editor) {
        return undefined as unknown as IKuToolSDKInstance['editor']['node'];
    }

    // 初始化节点增删改查能力
    const node = {
        insertNodesOnEmptyLine: (...args: any[]) => {
            return getExtensionNodeCommand(store).insertNodesOnEmptyLine(...args);
        },
        insertText: (...args: any[]) => {
            return getExtensionNodeCommand(store).insertText(...args);
        },
        deleteSelectionNode: (...args: any[]) => {
            return getExtensionNodeCommand(store).deleteSelectionNode(...args);
        },
        deleteForwardCharacter: () => {
            return getExtensionNodeCommand(store).deleteForward('character');
        },
        deleteForwardWord: () => {
            return getExtensionNodeCommand(store).deleteForward('word');
        },
        deleteForwardLine: () => {
            return getExtensionNodeCommand(store).deleteForward('line');
        },
        deleteForwardBlock: () => {
            return getExtensionNodeCommand(store).deleteForward('block');
        },
        deleteBackwardCharacter: () => {
            return getExtensionNodeCommand(store).deleteBackward('character');
        },
        deleteBackwardWord: () => {
            return getExtensionNodeCommand(store).deleteBackward('word');
        },
        deleteBackwardLine: () => {
            return getExtensionNodeCommand(store).deleteBackward('line');
        },
        deleteBackwardBlock: () => {
            return getExtensionNodeCommand(store).deleteBackward('block');
        },
        deleteSelectionTextProps: (...args: any[]) => {
            return getExtensionNodeCommand(store).deleteSelectionTextProps(...args);
        },
        updateSelectionNodeProps: (...args: any[]) => {
            return getExtensionNodeCommand(store).updateSelectionNodeProps(...args);
        },
        updateSelectionTextProps: (...args: any[]) => {
            return getExtensionNodeCommand(store).updateSelectionTextProps(...args);
        },
        getSelectionNode: (...args: any[]) => {
            return getExtensionNodeCommand(store).getSelectionNode(...args);
        },
        getSelectionTextNodes: (...args: any[]) => {
            return getExtensionNodeCommand(store).getSelectionTextNodes(...args);
        },
        getSelectionText: (...args: any[]) => {
            return getExtensionNodeCommand(store).getSelectionText(...args);
        },
        getNextNodeOfSelection: (...args: any[]) => {
            return getExtensionNodeCommand(store).getNextNodeOfSelection(...args);
        },
        getPreviousNodeOfSelection: (...args: any[]) => {
            return getExtensionNodeCommand(store).getPreviousNodeOfSelection(...args);
        },
        getParentNodeOfSelection: (...args: any[]) => {
            return getExtensionNodeCommand(store).getParentNodeOfSelection(...args);
        },
        getRootNode: () => {
            const editor = getEditor(store);
            if (!editor) {
                return null;
            }
            const node = {children: editor.children};
            return node;
        },
    };
    return node;
}

let editorCacheForSelectionCommand: any = null;
const selectionCommandCache: ExtensionSelectionCommand | null = null;
function getExtensionSelectionCommand(store: any): ExtensionSelectionCommand {
    const editor = getEditor(store);
    // 缓存非空，且最新获取的编辑器实例与缓存一致时，直接取缓存的 nodeCommand 实例
    if (selectionCommandCache && (editor === editorCacheForSelectionCommand)) {
        return selectionCommandCache;
    }

    // 记录一下最新获取的编辑器实例，作为下一次缓存判断的依据
    editorCacheForSelectionCommand = editor;
    const selectionCommand = new ExtensionSelectionCommand(editor);
    return selectionCommand;
}

function getSelection(store: any): IKuToolSDKInstance['editor']['selection'] {
    const editor = getEditor(store);

    // 理论上 editor 实例不可能为空，此处仅做保护性兜底处理
    if (!editor) {
        return undefined as unknown as IKuToolSDKInstance['editor']['selection'];
    }

    const selection: IKuToolSDKInstance['editor']['selection'] = {
        deselect: () => {
            return getExtensionSelectionCommand(store).deselect();
        },
        collapse: () => {
            return getExtensionSelectionCommand(store).collapse();
        },
        moveToStartOfTargetIndexCard: (...args: any[]) => {
            return getExtensionSelectionCommand(store).moveToStartOfTargetIndexCard(...args);
        },
        moveToEndOfTargetIndexCard: (...args: any[]) => {
            return getExtensionSelectionCommand(store).moveToEndOfTargetIndexCard(...args);
        },
        moveToStartOfCurrentBlock: () => {
            return getExtensionSelectionCommand(store).moveToStartOfCurrentBlock();
        },
        moveToEndOfCurrentBlock: () => {
            return getExtensionSelectionCommand(store).moveToEndOfCurrentBlock();
        },
        moveToStartOfCurrentCard: () => {
            return getExtensionSelectionCommand(store).moveToStartOfCurrentCard();
        },
        moveToEndOfCurrentCard: () => {
            return getExtensionSelectionCommand(store).moveToEndOfCurrentCard();
        },
        moveBackwardOffset: (...args: any[]) => {
            return getExtensionSelectionCommand(store).moveBackward('offset', ...args);
        },
        moveForwardOffset: (...args: any[]) => {
            return getExtensionSelectionCommand(store).moveForward('offset', ...args);
        },
        moveBackwardCharacter: (...args: any[]) => {
            return getExtensionSelectionCommand(store).moveBackward('character', ...args);
        },
        moveForwardCharacter: (...args: any[]) => {
            return getExtensionSelectionCommand(store).moveForward('character', ...args);
        },
        moveBackwardWord: (...args: any[]) => {
            return getExtensionSelectionCommand(store).moveBackward('word', ...args);
        },
        moveForwardWord: (...args: any[]) => {
            return getExtensionSelectionCommand(store).moveForward('word', ...args);
        },
        moveBackwardLine: (...args: any[]) => {
            return getExtensionSelectionCommand(store).moveBackward('line', ...args);
        },
        moveForwardLine: (...args: any[]) => {
            return getExtensionSelectionCommand(store).moveForward('line', ...args);
        },
        startPointMoveBackwardOffset: (...args: any[]) => {
            return getExtensionSelectionCommand(store).startPointMoveBackward('offset', ...args);
        },
        startPointMoveForwardOffset: (...args: any[]) => {
            return getExtensionSelectionCommand(store).startPointMoveForward('offset', ...args);
        },
        startPointMoveBackwardCharacter: (...args: any[]) => {
            return getExtensionSelectionCommand(store).startPointMoveBackward('character', ...args);
        },
        startPointMoveForwardCharacter: (...args: any[]) => {
            return getExtensionSelectionCommand(store).startPointMoveForward('character', ...args);
        },
        startPointMoveBackwardWord: (...args: any[]) => {
            return getExtensionSelectionCommand(store).startPointMoveBackward('word', ...args);
        },
        startPointMoveForwardWord: (...args: any[]) => {
            return getExtensionSelectionCommand(store).startPointMoveForward('word', ...args);
        },
        startPointMoveBackwardLine: (...args: any[]) => {
            return getExtensionSelectionCommand(store).startPointMoveBackward('line', ...args);
        },
        startPointMoveForwardLine: (...args: any[]) => {
            return getExtensionSelectionCommand(store).startPointMoveForward('line', ...args);
        },
        endPointMoveBackwardOffset: (...args: any[]) => {
            return getExtensionSelectionCommand(store).endPointMoveBackward('offset', ...args);
        },
        endPointMoveForwardOffset: (...args: any[]) => {
            return getExtensionSelectionCommand(store).endPointMoveForward('offset', ...args);
        },
        endPointMoveBackwardCharacter: (...args: any[]) => {
            return getExtensionSelectionCommand(store).endPointMoveBackward('character', ...args);
        },
        endPointMoveForwardCharacter: (...args: any[]) => {
            return getExtensionSelectionCommand(store).endPointMoveForward('character', ...args);
        },
        endPointMoveBackwardWord: (...args: any[]) => {
            return getExtensionSelectionCommand(store).endPointMoveBackward('word', ...args);
        },
        endPointMoveForwardWord: (...args: any[]) => {
            return getExtensionSelectionCommand(store).endPointMoveForward('word', ...args);
        },
        endPointMoveBackwardLine: (...args: any[]) => {
            return getExtensionSelectionCommand(store).endPointMoveBackward('line', ...args);
        },
        endPointMoveForwardLine: (...args: any[]) => {
            return getExtensionSelectionCommand(store).endPointMoveForward('line', ...args);
        },
        moveToStartOfLastTargetNode: (...args: any[]) => {
            return getExtensionSelectionCommand(store).moveToStartOfLastTargetNode(...args);
        },
        moveToEndOfLastTargetNode: (...args: any[]) => {
            return getExtensionSelectionCommand(store).moveToEndOfLastTargetNode(...args);
        },
        moveToStartOfNextTargetNode: (...args: any[]) => {
            return getExtensionSelectionCommand(store).moveToStartOfNextTargetNode(...args);
        },
        moveToEndOfNextTargetNode: (...args: any[]) => {
            return getExtensionSelectionCommand(store).moveToEndOfNextTargetNode(...args);
        },
    };
    return selection;
}

/**
 * 编辑器相关能力
 * 采用延迟加载的方式提供：sdk 赋能的这个过程，是在编辑器初始化前执行的，此时还未有编辑器实例，因此需要通过延迟加载的方式提供编辑器相关能力。
 * @param store 知识库 store 对象
 */
export function getEditorSdkGenerator(store: any): () => IKuToolSDKInstance['editor'] {
    return () => {
        const node = getNode(store);
        const MorphoAPI = getMorphoAPI(store);
        const selection = getSelection(store);

        const editorSdk: IKuToolSDKInstance['editor'] = {
            node,
            MorphoAPI,
            selection,
            jsonBuilder: JsonBuilder,
        };
        return editorSdk;
    };
}
